import { agentControllerConversationCapabilitySchema, resolveBuiltinAgentControllerConversationCapability } from "../daemon/agent-controller-conversation-capability.js";
import { normalizeKnownBuiltinAgentControllerId } from "../daemon/agent-controller-ref.js";
import { localAgentCatalogLaunchStatusSchema } from "../runtime/local-agent-catalog.js";
import { localComponentUpdatePolicySchema, localComponentUpdateStatusSchema, openMeldToolsMaintenanceProjectionSchema, openMeldToolsUpdateOperationSchema } from "../local-components.js";
import { agentControllerInterfaceCapabilitiesSchema, agentControllerInterfaceCheckSnapshotSchema, agentControllerInterfaceNativeInterfaceSchema, agentControllerInterfaceStatusSchema, agentControllerInterfaceVersionAdvisoryStatusSchema, agentControllerUsageLimitsSchema } from "../runtime/agent-controller-interface.js";
import { localRuntimeDeviceTypeSchema, localRuntimePlatformFamilySchema } from "./local-runtimes.js";
import { organizationMemberOpenMeldServiceStateSchema } from "./organization-member-openmeld-service-status.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/organization-computers.ts
const BOUNDED_ID_SCHEMA = z$1.string().trim().min(1).max(256);
const ISO_DATE_TIME_SCHEMA = z$1.iso.datetime({ offset: true });
const REASON_CODES_SCHEMA = z$1.array(BOUNDED_ID_SCHEMA).max(64);
const organizationComputerOpenMeldToolsSchema = z$1.strictObject({
	maintenance: openMeldToolsMaintenanceProjectionSchema.optional(),
	updateOperation: openMeldToolsUpdateOperationSchema.optional(),
	cli: z$1.strictObject({
		installedPackageVersion: BOUNDED_ID_SCHEMA.nullable(),
		latestPackageVersion: BOUNDED_ID_SCHEMA.nullable(),
		reasonCodes: REASON_CODES_SCHEMA,
		status: localComponentUpdateStatusSchema,
		updatePolicy: localComponentUpdatePolicySchema,
		wakeBlocked: z$1.boolean()
	}),
	service: z$1.strictObject({
		installedVersion: BOUNDED_ID_SCHEMA.nullable(),
		latestVersion: BOUNDED_ID_SCHEMA.nullable(),
		reasonCodes: REASON_CODES_SCHEMA,
		requiredVersion: BOUNDED_ID_SCHEMA.nullable(),
		status: localComponentUpdateStatusSchema,
		updatePolicy: localComponentUpdatePolicySchema,
		wakeBlocked: z$1.boolean()
	}),
	skills: z$1.strictObject({
		reasonCodes: REASON_CODES_SCHEMA,
		status: localComponentUpdateStatusSchema,
		targetCount: z$1.number().int().nonnegative().nullable(),
		updatePolicy: localComponentUpdatePolicySchema,
		wakeBlocked: z$1.boolean()
	})
});
const organizationComputerLocalAgentDetailsSchema = z$1.strictObject({
	capabilities: agentControllerInterfaceCapabilitiesSchema,
	check: agentControllerInterfaceCheckSnapshotSchema.optional(),
	checkedAt: ISO_DATE_TIME_SCHEMA,
	conversationCapabilities: agentControllerConversationCapabilitySchema.optional(),
	launch: z$1.strictObject({
		installChoiceCount: z$1.number().int().nonnegative(),
		status: localAgentCatalogLaunchStatusSchema
	}),
	models: z$1.array(z$1.strictObject({
		displayName: z$1.string().trim().min(1).max(256),
		id: BOUNDED_ID_SCHEMA.optional()
	})).max(256),
	nativeInterface: agentControllerInterfaceNativeInterfaceSchema,
	status: agentControllerInterfaceStatusSchema,
	usageLimits: agentControllerUsageLimitsSchema.optional(),
	version: BOUNDED_ID_SCHEMA.nullable(),
	versionAdvisory: z$1.strictObject({
		checkedAt: ISO_DATE_TIME_SCHEMA.nullable(),
		currentVersion: BOUNDED_ID_SCHEMA.nullable(),
		latestVersion: BOUNDED_ID_SCHEMA.nullable(),
		status: agentControllerInterfaceVersionAdvisoryStatusSchema
	}).optional()
});
function projectOrganizationComputerLocalAgentDetails(snapshot) {
	const builtinControllerId = normalizeKnownBuiltinAgentControllerId(snapshot.agentControllerRef);
	const conversationCapabilities = builtinControllerId ? resolveBuiltinAgentControllerConversationCapability(builtinControllerId) : null;
	return {
		capabilities: snapshot.capabilities,
		...snapshot.check ? { check: snapshot.check } : {},
		checkedAt: snapshot.checkedAt,
		...conversationCapabilities ? { conversationCapabilities } : {},
		launch: {
			installChoiceCount: snapshot.launch.installChoiceCount,
			status: snapshot.launch.status
		},
		models: snapshot.models.map((model) => ({
			displayName: model.displayName,
			id: model.id
		})),
		nativeInterface: snapshot.nativeInterface,
		status: snapshot.status,
		...snapshot.usageLimits ? { usageLimits: snapshot.usageLimits } : {},
		version: snapshot.version,
		...snapshot.versionAdvisory ? { versionAdvisory: {
			checkedAt: snapshot.versionAdvisory.checkedAt,
			currentVersion: snapshot.versionAdvisory.currentVersion,
			latestVersion: snapshot.versionAdvisory.latestVersion,
			status: snapshot.versionAdvisory.status
		} } : {}
	};
}
const organizationComputerLocalAgentSchema = z$1.strictObject({
	agentControllerRef: BOUNDED_ID_SCHEMA,
	details: organizationComputerLocalAgentDetailsSchema.nullable().optional(),
	enabled: z$1.boolean(),
	status: z$1.enum([
		"ready",
		"not_ready",
		"stale",
		"unknown"
	])
});
const organizationComputerSchema = z$1.strictObject({
	computerId: BOUNDED_ID_SCHEMA,
	deviceDisplayName: z$1.string().trim().min(1).max(256),
	deviceType: localRuntimeDeviceTypeSchema,
	lastSeenAtMs: z$1.number().int().nonnegative().nullable(),
	localAgents: z$1.array(organizationComputerLocalAgentSchema),
	openMeldTools: organizationComputerOpenMeldToolsSchema.nullable().optional(),
	osVersion: z$1.string().trim().min(1).max(120).nullable(),
	ownerUserId: BOUNDED_ID_SCHEMA,
	platformFamily: localRuntimePlatformFamilySchema,
	serviceState: organizationMemberOpenMeldServiceStateSchema
});
const organizationComputersResponseSchema = z$1.strictObject({
	computers: z$1.array(organizationComputerSchema),
	generatedAt: ISO_DATE_TIME_SCHEMA,
	organizationId: BOUNDED_ID_SCHEMA
});
//#endregion
export { organizationComputerLocalAgentDetailsSchema, organizationComputerLocalAgentSchema, organizationComputerOpenMeldToolsSchema, organizationComputerSchema, organizationComputersResponseSchema, projectOrganizationComputerLocalAgentDetails };

//# sourceMappingURL=organization-computers.js.map